import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { isParameterFiltered } from '@shared/http/http-param-filter';
import { environment } from 'environments/environment';
import { omitBy } from 'lodash-es';
import { Observable, map } from 'rxjs';
import {
  defaultPageSize,
  defaultSortColumn,
  defaultSortDirection,
} from '../components/receipt-overview/receipt-overview.component';
import {
  ExportReceiptsPayload,
  ExportStatus,
  Partners,
  ReceiptFile,
  ReceiptFilter,
  ReceiptType,
  ReceiptsResponse,
} from '../data/receipts.model';

@Injectable({
  providedIn: 'root',
})
export class ReceiptsService {
  private readonly http = inject(HttpClient);

  getTerminalManagementPartners(): Observable<Partners> {
    return this.http.get<Partners>(
      `${environment.bffTerminalManagementPortal}/receipt-management/payment-terminal-partners`
    );
  }

  getDirectPaymentPartners(): Observable<Partners> {
    return this.http.get<Partners>(
      `${environment.bffTerminalManagementPortal}/receipt-management/direct-payment-partners`
    );
  }

  getCardTypes(): Observable<string[]> {
    return this.http
      .get<{
        cardTypes: string[];
      }>(`${environment.bffTerminalManagementPortal}/receipt-management/card-types`)
      .pipe(map(response => response.cardTypes));
  }

  getReceipts(
    partnerId: string,
    filter: ReceiptFilter,
    receiptType: ReceiptType
  ): Observable<ReceiptsResponse> {
    const params = new HttpParams({
      fromObject: omitBy(
        {
          chargePointOperatorId: partnerId,
          terminalId: filter.terminalId,
          transactionId: filter.transactionId,
          cardNumberSuffix: filter.cardNumberSuffix,
          cardType: filter.cardType,
          chargePortName: filter.chargePortName,
          sessionStartFrom: filter.sessionStartFrom,
          sessionStartTo: filter.sessionStartTo,
          email: filter.email,
          sortColumn: filter.sortColumn || defaultSortColumn,
          sortOrder: filter.sortOrder || defaultSortDirection,
          itemsPerPage: filter.itemsPerPage || defaultPageSize,
          currentPage: filter.page || 0,
          type: receiptType,
        },
        p => isParameterFiltered(p)
      ),
    });

    return this.http.get<ReceiptsResponse>(
      `${environment.bffTerminalManagementPortal}/receipt-management/receipts`,
      {
        params,
      }
    );
  }

  downloadReceipt(
    partnerId: string,
    type: ReceiptType,
    downloadId: string
  ): Observable<ReceiptFile> {
    const params = new HttpParams({
      fromObject: omitBy(
        {
          chargePointOperatorId: partnerId,
          type: type,
        },
        p => isParameterFiltered(p)
      ),
    });

    return this.http.get<ReceiptFile>(
      `${environment.bffTerminalManagementPortal}/receipt-management/receipts/download/${downloadId}`,
      {
        params,
      }
    );
  }

  exportReceiptsAsZip(
    partnerId: string,
    type: ReceiptType,
    filter: ReceiptFilter
  ): Observable<Response> {
    const params = new HttpParams({
      fromObject: omitBy(
        {
          chargePointOperatorId: partnerId,
          type: type,
        },
        p => isParameterFiltered(p)
      ),
    });
    const payload: ExportReceiptsPayload = {
      terminalId: filter.terminalId || null,
      transactionId: filter.transactionId || null,
      cardNumberSuffix: filter.cardNumberSuffix || null,
      cardType: filter.cardType || null,
      chargepointId: filter.chargePortName || null,
      startTimestampFrom: filter.sessionStartFrom || null,
      startTimestampTo: filter.sessionStartTo || null,
    };

    return this.http.post<Response>(
      `${environment.bffTerminalManagementPortal}/receipt-management/receipts/${partnerId}`,
      payload,
      {
        params,
      }
    );
  }

  getZipExportStatus(partnerId: string, type: ReceiptType): Observable<ExportStatus> {
    const params = new HttpParams({
      fromObject: omitBy(
        {
          type: type,
        },
        p => isParameterFiltered(p)
      ),
    });
    return this.http.get<ExportStatus>(
      `${environment.bffTerminalManagementPortal}/receipt-management/receipts/${partnerId}`,
      {
        params,
      }
    );
  }
}
